/** @format */

import dayjs from 'dayjs'
import {
  AGGREGATE_TYPE_24H_SEC,
  DEFAULT_AGGREGATE_TYPE_SEC
} from 'src/pages/AIObservability/components/constant'
import type { OverviewLineChartPoint } from 'src/pages/AIObservability/components/OverviewLineChart'

const MS_PER_DAY = 24 * 3600 * 1000
const TREND_TIMESTAMPS_HARD_CAP = 500

/**
 * X 轴刻度文案：
 * - 跨度不足 24 小时：HH:mm
 * - 跨度 ≥24 小时且聚合粒度 &lt;24h：MM-DD HH:mm
 * - 跨度 ≥24 小时且 24 小时粒度：MM-DD
 */
export function getOverviewAxisTimeFormat(rangeMsAbs: number, aggregateSeconds: number): string {
  if (rangeMsAbs < MS_PER_DAY) return 'HH:mm'
  if (aggregateSeconds >= AGGREGATE_TYPE_24H_SEC) return 'MM-DD'
  return 'MM-DD HH:mm'
}

/**
 * 按聚合步长在 [startMs,endMs] 上生成刻度时间戳（毫秒，升序去重）。
 */
export function buildOverviewTrendTimestamps(
  startMs: number,
  endMs: number,
  aggregateSeconds: number
): number[] {
  const aggSec = aggregateSeconds > 0 ? aggregateSeconds : DEFAULT_AGGREGATE_TYPE_SEC
  const lo = Math.min(startMs, endMs)
  const hi = Math.max(startMs, endMs)
  const span = hi - lo
  if (!(span >= 0)) return [Math.round(lo)]
  const stepMs = Math.max(1000, aggSec * 1000)
  const first = Math.ceil(lo / stepMs) * stepMs
  const out: number[] = []
  for (let t = first; t <= hi && out.length < TREND_TIMESTAMPS_HARD_CAP; t += stepMs) {
    out.push(Math.round(t))
  }
  if (!out.length) return [Math.round(lo), Math.round(hi)]
  if (out[0] > lo + stepMs * 0.001) out.unshift(Math.round(lo))
  if (out[out.length - 1] < hi - stepMs * 0.001) out.push(Math.round(hi))
  return Array.from(new Set(out)).sort((a, b) => a - b)
}

function parsePointTimestampMs(timestamp: string | number): number {
  const num = Number(timestamp)
  if (!Number.isNaN(num) && String(timestamp).trim() !== '') {
    return num < 1e12 ? num * 1000 : num
  }
  return dayjs(timestamp).valueOf()
}

/**
 * 按 [startTime, endTime] 与 aggregate（秒）对齐时间轴，补齐 Points；无数据点 Value 为 null。
 * startTime / endTime 与接口一致，单位为秒。
 */
export function fillMetricPoints(
  points: { Timestamp: string; Value?: number | null }[] | undefined,
  startTimeSec: number,
  endTimeSec: number,
  aggregateSec: number
): OverviewLineChartPoint[] {
  const startMs = startTimeSec * 1000
  const endMs = endTimeSec * 1000
  const slots = buildOverviewTrendTimestamps(startMs, endMs, aggregateSec)
  const stepMs = Math.max(
    1000,
    (aggregateSec > 0 ? aggregateSec : DEFAULT_AGGREGATE_TYPE_SEC) * 1000
  )
  const halfStep = stepMs / 2

  const valueBySlot = new Map<number, number | null>()
  for (const point of points ?? []) {
    const tsMs = parsePointTimestampMs(point.Timestamp)
    if (!Number.isFinite(tsMs)) {
      continue
    }
    const slot = slots.find(t => Math.abs(t - tsMs) <= halfStep) ?? tsMs
    const value = point.Value === null || point.Value === undefined ? null : Number(point.Value)
    valueBySlot.set(slot, Number.isNaN(value as number) ? null : value)
  }
  return slots.map(slotMs => ({
    Timestamp: String(slotMs),
    Value: valueBySlot.get(slotMs) ?? null
  }))
}
